import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { APP_LANGUAGES } from '@core/constants/app-languages';
import { AppTranslateLanguageService } from '@core/services/app-translate-language.service';
import { DirectionService } from '@core/services/direction.service';
import { TranslateService } from '@ngx-translate/core';
import { SharedModule } from '@shared/shared.module';
import Keycloak from 'keycloak-js';
import { environment } from '@env/environment';

@Component({
  selector: 'app-select-keycloak-realm',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './select-keycloak-realm.component.html',
  styleUrls: ['./select-keycloak-realm.component.scss'],
})
export class SelectKeycloakRealmComponent implements OnInit {
  private readonly translate = inject(TranslateService);
  private readonly langService = inject(AppTranslateLanguageService);
  private readonly directionService = inject(DirectionService);
  private readonly fb = inject(FormBuilder);

  readonly isRtl = this.directionService.isRtl;

  readonly realmForm: FormGroup = this.fb.group({
    realm: ['', [Validators.required]],
    selectedLanguage: ['tr'],
  });

  readonly languageOptions = APP_LANGUAGES.map(({ labelKey, value }) => ({ labelKey, value }));

  private readonly langTick = signal(0);

  readonly languages = computed(() => {
    this.langTick();
    return this.languageOptions.map((opt) => ({
      label: this.translate.instant(opt.labelKey),
      value: opt.value,
    }));
  });

  readonly errorStatus = signal(false);
  readonly alertShow = signal(false);
  readonly errorMessage = signal('');
  readonly showLoading = signal(false);

  ngOnInit(): void {
    const lang = this.langService.getStoredLanguage();
    this.setForm({ selectedLanguage: lang });
    this.langService.useLanguage(lang);
    this.translate.onLangChange.subscribe(() => this.langTick.update((n) => n + 1));
    this.realmForm.get('selectedLanguage')?.valueChanges.subscribe((lang) => {
      if (lang) {
        this.langService.useLanguage(lang);
        this.langTick.update((n) => n + 1);
      }
    });
  }

  /** Apply defaults; pass partial values when restoring from storage or a loaded record. */
  private setForm(values?: Partial<{ realm: string; selectedLanguage: string }>): void {
    this.realmForm.reset(
      {
        realm: values?.realm ?? '',
        selectedLanguage: values?.selectedLanguage ?? 'tr',
      },
      { emitEvent: false }
    );
  }

  onSubmit(): void {
    if (this.realmForm.valid) {
      const { realm, selectedLanguage } = this.realmForm.value;
      this.showLoading.set(true);

      localStorage.setItem('realm', realm);

      const keycloak = new Keycloak({
        url: environment.keycloakUrl,
        realm,
        clientId: environment.keycloakClientId,
      });

      keycloak
        .init({ checkLoginIframe: false })
        .then(() => {
          keycloak.login({
            redirectUri: window.location.origin + '/auth/select-company',
            locale: selectedLanguage,
          });
        })
        .catch((initError: unknown) => {
          this.handleError('company_name_tax_no_not_found');
          console.error(initError);
        })
        .finally(() => {
          this.showLoading.set(false);
        });
    } else {
      this.realmForm.markAllAsTouched();
      this.alertShow.set(true);
    }
  }

  handleError(errorKey?: string): void {
    this.errorMessage.set(errorKey ?? 'could_not_connect_server');
    this.errorStatus.set(true);
    this.showLoading.set(false);
  }
}
